import { AuthenticationUser, AuthRoleEnum, useLoginValidateMutation } from '@codezo/graphql';
import { Center, Loader, Stack, Text } from '@mantine/core';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { scheduleTokenRefresh } from '../utils/tokenScheduler';
import { storeRefreshToken } from '../utils/tokenStorage';

export interface AuthCallbackPageProps {
	/**
	 * Required role(s) for this platform
	 * Can be a single role or array of roles
	 */
	requiredRole: AuthRoleEnum | AuthRoleEnum[];
	/**
	 * Where to redirect after successful authentication
	 * @default '/dashboard'
	 */
	successRedirect?: string;
	/**
	 * Set user and access token in context
	 */
	setUser: (_user: AuthenticationUser) => void;
	/**
	 * Set access token in context
	 */
	setAccessToken: (_token: string) => void;
}

export function AuthCallbackPage({
	requiredRole,
	successRedirect = '/dashboard',
	setUser,
	setAccessToken,
}: AuthCallbackPageProps) {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const validationInitiated = useRef(false);

	const [loginValidate] = useLoginValidateMutation({
		onError: () => {
			// Removed console.log
			navigate('/404');
		},
	});

	useEffect(() => {
		// Prevent double execution
		if (validationInitiated.current) {
			return;
		}

		const validateLogin = async () => {
			const token = searchParams.get('token');

			if (!token) {
				navigate('/404');
				return;
			}

			// Mark validation as initiated immediately
			validationInitiated.current = true;

			try {
				const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

				const result = await loginValidate({
					variables: {
						input: {
							token,
							role: roles,
						},
					},
				});

				if (result.data?.loginValidate) {
					const { user, token: accessToken, refreshToken } = result.data.loginValidate;

					// Check if user has required role
					if (!roles.includes(user.role)) {
						window.location.replace(
							import.meta.env.VITE_PUBLIC_APP_URL || '/auth/login'
						);
						return;
					}
					// Set user and access token in context
					setUser(user as AuthenticationUser);
					setAccessToken(accessToken);

					// Store refresh token for re-authentication
					storeRefreshToken(refreshToken);

					// Schedule automatic token refresh
					scheduleTokenRefresh(accessToken);

					// Navigate to success redirect
					navigate(successRedirect, { replace: true });
				}
			} catch {
				// Error handled by mutation onError
				// Error is already handled by onError callback
			}
		};

		validateLogin();
	}, [
		loginValidate,
		navigate,
		requiredRole,
		searchParams,
		setAccessToken,
		setUser,
		successRedirect,
	]);

	return (
		<Center h="100vh">
			<Stack align="center" gap="lg">
				<Loader size="lg" color="codezo" />
				<Stack align="center" gap="xs">
					<Text size="lg" fw={500}>
						Authenticating...
					</Text>
					<Text size="sm" c="dimmed">
						Please wait while we validate your credentials.
					</Text>
				</Stack>
			</Stack>
		</Center>
	);
}
